import { useEffect, useRef } from "react";

/**
 * Orange highlighter pen behind one key phrase. Use it once per heading, on the words that matter most.
 * The marker is drawn by default (so it shows without JavaScript); with JavaScript it sweeps in when scrolled into view.
 * Rendered as <mark> so the emphasis is meaningful to assistive tech and search engines.
 */
export function Highlight({ children, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") return undefined;
    el.setAttribute("data-armed", "true");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.setAttribute("data-in", "true");
          observer.disconnect();
        }
      },
      { threshold: 0.6 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <mark ref={ref} className={`hl ${className}`}>
      {children}
    </mark>
  );
}

/** Splits `text` around `phrase` and wraps the phrase in <Highlight>. Returns plain text if the phrase is not found. */
export function withHighlight(text, phrase) {
  if (!phrase || typeof text !== "string") return text;
  const index = text.indexOf(phrase);
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <Highlight>{phrase}</Highlight>
      {text.slice(index + phrase.length)}
    </>
  );
}
